import React, { useEffect, useState } from "react";
import {
  Camera,
  ChevronDown,
  ChevronRight,
  FlipHorizontal,
  RefreshCw,
  RotateCcw,
  Flashlight,
  Sun,
  Contrast,
  Droplet,
  Monitor,
  SwitchCamera,
} from "lucide-react";
import { toast } from "sonner";
import { cameraSupportsTorch, setCameraTorch } from "../lib/useCameraCompositor";

export const DEFAULT_CAM_SETTINGS = {
  rotationDeg: 0,
  mirrorH: false,
  brightness: 1,
  contrast: 1,
  saturation: 1,
  torch: false,
  resolution: "1080p", // 720p | 1080p | 4k
  fps: 30, // 30 | 60
};

const RESOLUTIONS = {
  "720p": { width: 1280, height: 720, label: "HD · 720p" },
  "1080p": { width: 1920, height: 1080, label: "Full HD · 1080p" },
  "4k": { width: 3840, height: 2160, label: "4K · 2160p" },
};

/**
 * CameraSettingsPanel — pro-level camera controls for the Director Console.
 * Exposes:
 *   • Device flip (front <-> back on mobile, next camera on desktop)
 *   • Horizontal mirror (selfie flip)
 *   • Rotation: 0 / 90 / 180 / 270 presets + freeform slider
 *   • Flashlight (torch) — mobile only, grays out when unsupported
 *   • Resolution: 720p / 1080p / 4K — triggers media re-acquire
 *   • Frame rate: 30 / 60 fps — triggers media re-acquire
 *   • Brightness / Contrast / Saturation sliders
 *
 * Props:
 *   - settings: current settings object
 *   - onChange(next): fires on any change (caller persists + applies)
 *   - mediaStream: current MediaStream (used for torch + capability checks)
 *   - onFlip: callback to flip camera device (usually media.flipCamera)
 *   - onReacquire({width,height,fps}): callback to re-acquire with new constraints
 *   - defaultOpen: whether the collapsible section starts expanded
 */
export default function CameraSettingsPanel({
  settings,
  onChange,
  mediaStream,
  onFlip,
  onReacquire,
  defaultOpen = false,
  compact = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [torchCapable, setTorchCapable] = useState(false);
  const s = { ...DEFAULT_CAM_SETTINGS, ...(settings || {}) };

  useEffect(() => {
    setTorchCapable(cameraSupportsTorch(mediaStream));
  }, [mediaStream]);

  const update = (patch) => onChange?.({ ...s, ...patch });

  const applyTorch = async (on) => {
    const ok = await setCameraTorch(mediaStream, on);
    if (ok) {
      update({ torch: on });
      toast.success(`Flashlight ${on ? "on" : "off"}`, { id: "torch" });
    } else {
      toast.info("Flashlight not supported on this camera", { id: "torch" });
    }
  };

  const applyResolution = (key) => {
    const r = RESOLUTIONS[key];
    if (!r) return;
    update({ resolution: key });
    try { onReacquire?.({ width: r.width, height: r.height, fps: s.fps }); } catch {}
  };

  const applyFps = (fps) => {
    update({ fps });
    const r = RESOLUTIONS[s.resolution] || RESOLUTIONS["1080p"];
    try { onReacquire?.({ width: r.width, height: r.height, fps }); } catch {}
  };

  const resetAll = () => {
    onChange?.({ ...DEFAULT_CAM_SETTINGS });
    toast.info("Camera settings reset", { id: "cam-reset" });
  };

  return (
    <section
      data-testid="camera-settings-panel"
      className={`${compact ? "p-2" : "p-2.5"} bg-black/40 border border-white/10 rounded-lg`}
    >
      <button
        data-testid="camera-settings-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-black text-white/60 hover:text-white"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Camera className="w-3 h-3 text-tv-accent" /> Camera Settings
        {(s.rotationDeg !== 0 || s.mirrorH || s.torch || s.brightness !== 1 || s.contrast !== 1 || s.saturation !== 1) && (
          <span className="ml-auto text-[8px] font-black text-tv-accent">EDITED</span>
        )}
      </button>

      {open && (
        <div className="mt-2 space-y-2.5">
          {/* Device controls */}
          <div className="grid grid-cols-2 gap-1.5">
            <button
              data-testid="cam-flip-device"
              onClick={onFlip}
              className="flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 text-white text-[10px] font-bold py-2 rounded border border-white/10"
              title="Switch between front and back cameras"
            >
              <SwitchCamera className="w-3.5 h-3.5 text-tv-accent" /> Flip Cam
            </button>
            <button
              data-testid="cam-flip-mirror"
              onClick={() => update({ mirrorH: !s.mirrorH })}
              aria-pressed={s.mirrorH}
              className={`flex items-center justify-center gap-1.5 text-[10px] font-bold py-2 rounded border ${s.mirrorH ? "bg-tv-accent text-black border-tv-accent" : "bg-white/5 text-white border-white/10 hover:bg-white/10"}`}
              title="Selfie-mirror horizontal flip"
            >
              <FlipHorizontal className="w-3.5 h-3.5" /> Mirror
            </button>
          </div>

          {/* Rotation presets */}
          <div>
            <div className="text-[9px] uppercase tracking-wider font-black text-white/50 mb-1 flex items-center justify-between">
              <span className="flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Rotation</span>
              <span className="text-white/70 tabular-nums">{Math.round(s.rotationDeg)}°</span>
            </div>
            <div className="grid grid-cols-4 gap-1 mb-1">
              {[0, 90, 180, 270].map((deg) => {
                const active = Math.round(s.rotationDeg) === deg;
                return (
                  <button
                    key={deg}
                    data-testid={`cam-rotate-${deg}`}
                    onClick={() => update({ rotationDeg: deg })}
                    className={`text-[10px] font-bold py-1.5 rounded border ${active ? "bg-tv-accent text-black border-tv-accent" : "bg-white/5 text-white border-white/10 hover:bg-white/10"}`}
                  >
                    {deg}°
                  </button>
                );
              })}
            </div>
            <input
              data-testid="cam-rotate-slider"
              type="range"
              min="0"
              max="360"
              step="1"
              value={s.rotationDeg}
              onChange={(e) => update({ rotationDeg: parseInt(e.target.value, 10) })}
              className="w-full accent-tv-accent"
              aria-label="Rotation degrees"
            />
          </div>

          {/* Resolution + FPS */}
          <div className="grid grid-cols-3 gap-1">
            {Object.entries(RESOLUTIONS).map(([k, r]) => {
              const active = s.resolution === k;
              return (
                <button
                  key={k}
                  data-testid={`cam-res-${k}`}
                  onClick={() => applyResolution(k)}
                  className={`text-[10px] font-bold py-1.5 rounded border ${active ? "bg-emerald-500 text-black border-emerald-400" : "bg-white/5 text-white border-white/10 hover:bg-white/10"}`}
                  title={r.label}
                >
                  {k.toUpperCase()}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-1">
            {[30, 60].map((fps) => {
              const active = s.fps === fps;
              return (
                <button
                  key={fps}
                  data-testid={`cam-fps-${fps}`}
                  onClick={() => applyFps(fps)}
                  className={`text-[10px] font-bold py-1.5 rounded border ${active ? "bg-sky-500 text-white border-sky-400" : "bg-white/5 text-white border-white/10 hover:bg-white/10"}`}
                >
                  <Monitor className="w-3 h-3 inline mr-1" /> {fps} FPS
                </button>
              );
            })}
          </div>

          {/* Flashlight */}
          <button
            data-testid="cam-torch-toggle"
            onClick={() => applyTorch(!s.torch)}
            disabled={!torchCapable}
            aria-pressed={s.torch}
            className={`w-full flex items-center justify-center gap-1.5 text-[10px] font-bold py-2 rounded border transition ${
              !torchCapable
                ? "bg-white/5 text-white/30 border-white/10 cursor-not-allowed"
                : s.torch
                  ? "bg-amber-400 text-black border-amber-300 shadow-[0_0_12px_rgba(251,191,36,0.5)]"
                  : "bg-white/5 text-white border-white/10 hover:bg-white/10"
            }`}
            title={torchCapable ? "Toggle flashlight (mobile rear camera)" : "Flashlight not supported on this camera"}
          >
            <Flashlight className="w-3.5 h-3.5" /> {torchCapable ? (s.torch ? "Flashlight on" : "Flashlight") : "Flashlight n/a"}
          </button>

          {/* Color sliders */}
          <SliderRow
            testid="cam-brightness"
            label="Brightness"
            Icon={Sun}
            value={s.brightness}
            min={0.3}
            max={2}
            step={0.05}
            onChange={(v) => update({ brightness: v })}
          />
          <SliderRow
            testid="cam-contrast"
            label="Contrast"
            Icon={Contrast}
            value={s.contrast}
            min={0.3}
            max={2}
            step={0.05}
            onChange={(v) => update({ contrast: v })}
          />
          <SliderRow
            testid="cam-saturation"
            label="Saturation"
            Icon={Droplet}
            value={s.saturation}
            min={0}
            max={2}
            step={0.05}
            onChange={(v) => update({ saturation: v })}
          />

          <button
            data-testid="cam-settings-reset"
            onClick={resetAll}
            className="w-full flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 text-white/70 text-[10px] font-bold py-1.5 rounded"
          >
            <RefreshCw className="w-3 h-3" /> Reset all
          </button>
        </div>
      )}
    </section>
  );
}

function SliderRow({ testid, label, Icon, value, min, max, step, onChange }) {
  const pct = Math.round((value / 2) * 100);
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider font-black text-white/50 mb-0.5 flex items-center justify-between">
        <span className="flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</span>
        <span className="text-white/70 tabular-nums">{pct}%</span>
      </div>
      <input
        data-testid={testid}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-tv-accent"
        aria-label={label}
      />
    </div>
  );
}
